<script setup lang="ts">
    import { useGlobalConfig } from '~/composables/storyblok-proxy/useGlobalConfig';
    import { normalizeCountryEntries } from '~/utils/countryConfig';
    import type { CountryListItem } from '~/utils/countryConfig';

    const { modalOpen = false } = defineProps<{
        modalOpen?: boolean;
    }>();
    const { lang, country } = useLanguage();
    const { t, te, setLocale } = useI18n();
    const { commitCountryLanguage } = useCountryLanguageSwitch();
    const draftMode = useDraftMode();

    const modalId = useId();
    const languagePickerModalController = getOrCreateModalController(modalId);

    type FocusableElement = { focus: () => void };

    const switchButton = ref<HTMLElement | null>(null);
    const closeButtonRef = ref<FocusableElement | null>(null);

    const handleOpen = () => {
        if (!languagePickerModalController.isOpen.value) {
            pendingCountryCode.value = country.value;
            pendingLanguageCode.value = lang.value;

            languagePickerModalController.open();

            nextTick(() => {
                closeButtonRef.value?.focus?.();
            });
        }
    };

    const closeModal = () => {
        languagePickerModalController.close();
    };

    watch([closeButtonRef], () => {
        if (!languagePickerModalController.isOpen.value) {
            switchButton.value?.focus?.();
        }
    });

    const { data: globalCountryConfigStory } = await useGlobalConfig<CountryConfigStoryblok>('globalcountryconfig', {
        cv: 0
    });

    const rawCountryEntries = computed<CountryEntryStoryblok[]>(() => {
        return globalCountryConfigStory.value?.content?.CountryList || [];
    });

    const countriesList = computed<CountryListItem[]>(() => {
        return normalizeCountryEntries(rawCountryEntries.value).map((country) => {
            const key = `languagePicker.locations.${country.key}`;

            return {
                ...country,
                name: te(key) ? t(key) : country.name
            };
        });
    });

    const globalCountryCode = 'global';

    const globalCountryEntry = computed(() => {
        return countriesList.value.find((countryItem) => countryItem.key === globalCountryCode);
    });

    const countries = computed<CountryListItem[]>(() => {
        return countriesList.value.filter((countryItem) => countryItem.key !== globalCountryCode);
    });

    const countriesByCode = computed<Record<string, CountryListItem>>(() => {
        const map: Record<string, CountryListItem> = {};
        for (const country of countriesList.value) map[country.key] = country;
        return map;
    });

    const activeCountry = computed(() => {
        const entry = countriesByCode.value[country.value];
        return {
            key: country.value,
            name: entry?.name ?? t(`languagePicker.locations.${country.value}`),
            flagUrl: entry?.flagUrl,
            flagAlt: entry?.flagAlt
        };
    });

    const pendingCountryCode = ref<string>(country.value);
    const pendingLanguageCode = ref<string>(lang.value);

    const setPendingCountry = (countryCode: string) => {
        pendingCountryCode.value = countryCode;

        const mode = draftMode.isDraftModeEnabled.value ? StoryblokVersion.Draft : StoryblokVersion.Published;
        const defaultLanguageValue = getDefaultLanguageForCountry(countryCode, mode);
        if (defaultLanguageValue) {
            pendingLanguageCode.value = defaultLanguageValue;
        }
    };

    const setPendingLanguage = (countryCode: string, languageCode: string) => {
        pendingCountryCode.value = countryCode;
        pendingLanguageCode.value = languageCode;
    };

    const selectCountryLanguage = async (countryCode: string, langCode: string) => {
        setPendingLanguage(countryCode, langCode);
    };

    const selectCountryDefaultLanguage = async (countryCode: string) => {
        setPendingCountry(countryCode);
    };

    const confirmSelection = async () => {
        if (!pendingCountryCode.value || !pendingLanguageCode.value) return;

        await commitCountryLanguage(pendingCountryCode.value, pendingLanguageCode.value);
        closeModal();
    };

    watch(lang, async (newLocale) => {
        // @ts-expect-error - setLocale expects either "de" or "en", but lang is typed as string
        if (newLocale) await setLocale(newLocale);
    });
</script>

<template>
    <div
        class="languages-picker"
        :class="modalOpen ? '--modal' : ''"
    >
        <button
            ref="switchButton"
            @click="handleOpen"
            @keydown.space.prevent="handleOpen"
        >
            <span class="sr-only"> {{ $t('languagePicker.chosenCountry') }}: {{ activeCountry.name }} </span>
            <span class="sr-only">
                {{ $t('languagePicker.chosenLanguage') }}:
                {{ $t(`languagePicker.languages.${lang}`) }}
            </span>

            <div
                class="picker-summary"
                aria-hidden="true"
            >
                <GlobeIcon
                    v-if="activeCountry.key.toLowerCase() === 'global'"
                    aria-hidden="true"
                    class="globe-icon"
                />

                <img
                    v-else-if="activeCountry.flagUrl"
                    class="picker-flag"
                    :src="activeCountry.flagUrl"
                    :alt="activeCountry.flagAlt"
                />
                <span class="picker-country">{{ activeCountry.name }}</span>
                <span class="picker-separator">|</span>
                <span class="picker-lang">
                    {{ lang.toUpperCase() }}
                </span>
            </div>
        </button>
    </div>

    <ClientOnly>
        <SharedModal
            :controller="languagePickerModalController"
            :is-story-blok-modal="false"
            :no-padding="true"
            class="languages-modal"
            :modal-id="modalId"
        >
            <div class="content">
                <div class="header">
                    <h2>{{ $t('languagePicker.globalHeadline') }}</h2>
                    <SharedCloseButton
                        ref="closeButtonRef"
                        :label="$t('languagePicker.close')"
                        :icon-inverted="true"
                        @click="closeModal"
                    />
                </div>

                <div class="country-list --global">
                    <HeaderLanguagePickerCountryItem
                        v-if="globalCountryEntry"
                        :country="globalCountryEntry"
                        :is-global="true"
                        :pending-country-code="pendingCountryCode"
                        :pending-language-code="pendingLanguageCode"
                        @select-country-default-language="selectCountryDefaultLanguage"
                        @select-country-language="selectCountryLanguage"
                    />
                </div>

                <div class="header">
                    <h3>{{ $t('languagePicker.headline') }}</h3>
                </div>

                <div
                    class="country-list"
                    role="radiogroup"
                >
                    <HeaderLanguagePickerCountryItem
                        v-for="countryItem in countries"
                        :key="countryItem.key"
                        :country="countryItem"
                        :pending-country-code="pendingCountryCode"
                        :pending-language-code="pendingLanguageCode"
                        @select-country-default-language="selectCountryDefaultLanguage"
                        @select-country-language="selectCountryLanguage"
                    />
                </div>

                <div class="modal-actions">
                    <PrimaryButton
                        id="confirm-selection-button"
                        :button-text="$t('languagePicker.save')"
                        product-button
                        @click="confirmSelection"
                    />
                </div>
            </div>
        </SharedModal>

        <template #fallback>
            <span style="display: none" />
        </template>
    </ClientOnly>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/navigation/header-language-picker.scss';
</style>
